/**
 * StreamingCounter — animated token amount display.
 *
 * Uses SmoothDisplayEngine for continuous interpolation between signed states.
 */

import { useState, useEffect, useRef, type ReactNode } from 'react';
import { formatTokenAmount } from '@tokenstreaming/shared';
import type { StreamingEngine } from '../modules/streaming/types.js';

interface Props {
  engine: StreamingEngine;
  tokenSymbol: string;
  tokenDecimals: number;
  deposit: bigint;
  role: 'sender' | 'receiver';
}

export function StreamingCounter({ engine, tokenSymbol, tokenDecimals, deposit, role }: Props): ReactNode {
  const [displayAmount, setDisplayAmount] = useState('0.000000');
  const [elapsed, setElapsed] = useState('0:00');
  const [engineState, setEngineState] = useState(engine.getState());
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const update = () => {
      // Update engine state for UI feedback
      setEngineState(engine.getState());
      
      const channelState = engine.getChannelState();
      if (channelState) {
        const amount = role === 'sender'
          ? channelState.resolverAmount  // sender sees how much they've sent
          : channelState.resolverAmount; // receiver sees how much they've received
        setDisplayAmount(formatTokenAmount(amount, tokenDecimals));
      }

      const ms = engine.getElapsedMs();
      const totalSeconds = Math.floor(ms / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      setElapsed(`${minutes}:${seconds.toString().padStart(2, '0')}`);

      rafRef.current = requestAnimationFrame(update);
    };

    rafRef.current = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafRef.current);
  }, [engine, role, tokenDecimals]);

  const remaining = deposit > 0n
    ? formatTokenAmount(deposit - (engine.getChannelState()?.resolverAmount ?? 0n), tokenDecimals)
    : '0.000000';

  // Show status message based on engine state
  const statusMessage = engineState === 'negotiating' 
    ? 'Negotiating channel...'
    : engineState === 'streaming'
    ? null
    : `Status: ${engineState}`;

  return (
    <div className="streaming-counter">
      <div className="counter-label">
        {role === 'sender' ? 'Streamed' : 'Received'}
      </div>
      <div className="counter-amount">
        <span className="counter-value">{displayAmount}</span>
        <span className="counter-symbol">{tokenSymbol}</span>
      </div>
      {statusMessage && (
        <div className="counter-status" style={{ fontSize: '0.9em', color: '#888', marginTop: '0.5em' }}>
          {statusMessage}
        </div>
      )}
      <div className="counter-meta">
        <span className="counter-elapsed">{elapsed}</span>
        <span className="counter-remaining">
          {role === 'sender' ? `${remaining} remaining` : ''}
        </span>
      </div>
    </div>
  );
}
